/**
 * ShareComparisonDialog Component
 *
 * Dialog for creating a shareable link for a running comparison.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { ContentCopy as CopyIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import { shareRunningComparison } from '../../api/runningShareApi';

export default function ShareComparisonDialog({ open, onClose, comparisonData }) {
  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [labelA, setLabelA] = useState(comparisonData?.labelA || '');
  const [labelB, setLabelB] = useState(comparisonData?.labelB || '');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    setLoading(true);
    setError(null);

    try {
      const shareData = {
        base_session_id: comparisonData.base_session_id,
        is_anonymous: isAnonymous,
        display_name: displayName || null,
        description: description || null,
        label_a: labelA || null,
        label_b: labelB || null,
      };

      // Add comparison source (one of three)
      if (comparisonData.comparison_fit_file) {
        shareData.comparison_fit_file = comparisonData.comparison_fit_file;
      }
      if (comparisonData.comparison_file_id) {
        shareData.comparison_file_id = comparisonData.comparison_file_id;
      }
      if (comparisonData.comparison_shared_id) {
        shareData.comparison_shared_id = comparisonData.comparison_shared_id;
      }
      if (comparisonData.comparison_prediction_id) {
        shareData.comparison_prediction_id = comparisonData.comparison_prediction_id;
      }
      if (comparisonData.snap_b_to_gpx !== undefined) {
        shareData.snap_b_to_gpx = comparisonData.snap_b_to_gpx;
      }

      const result = await shareRunningComparison(shareData);

      // Build full URL
      const fullUrl = `${window.location.origin}${result.share_url}`;
      setShareUrl(fullUrl);
    } catch (err) {
      console.error('Failed to share comparison:', err);
      setError(err.response?.data?.detail || 'Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (shareUrl) {
      navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleViewShared = () => {
    if (shareUrl) {
      window.open(shareUrl, '_blank');
    }
  };

  const handleClose = () => {
    setDisplayName('');
    setDescription('');
    setLabelA(comparisonData?.labelA || '');
    setLabelB(comparisonData?.labelB || '');
    setIsAnonymous(false);
    setShareUrl(null);
    setError(null);
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share Running Comparison</DialogTitle>
      <DialogContent>
        {!shareUrl ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Display Name (optional)"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              placeholder="My UTMB 2023 vs 2024 Comparison"
              helperText="Public name for this comparison"
            />

            <TextField
              label="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="Comparing my performance improvements..."
            />

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="Workout A Label (optional)"
                value={labelA}
                onChange={(e) => setLabelA(e.target.value)}
                fullWidth
                placeholder="My Run (2023)"
              />
              <TextField
                label="Workout B Label (optional)"
                value={labelB}
                onChange={(e) => setLabelB(e.target.value)}
                fullWidth
                placeholder="My Run (2024)"
              />
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={isAnonymous}
                  onChange={(e) => setIsAnonymous(e.target.checked)}
                />
              }
              label="Share anonymously (hide my name)"
            />

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Alert severity="info">
              This will execute the comparison and save the result. May take a few seconds.
            </Alert>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="success">
              Share link created successfully!
            </Alert>

            <TextField
              label="Shareable Link"
              value={shareUrl}
              fullWidth
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} edge="end" title="Copy link">
                      <CopyIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {copied && (
              <Typography variant="body2" color="success.main">
                ✓ Link copied to clipboard!
              </Typography>
            )}

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<OpenIcon />}
                onClick={handleViewShared}
                fullWidth
              >
                View Shared Page
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {!shareUrl ? (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleShare}
              disabled={loading}
              startIcon={loading && <CircularProgress size={16} />}
            >
              {loading ? 'Creating...' : 'Create Share Link'}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
